/**
 * The preload manager behind the loading scene: every step is planned up front with a weight, so
 * the bar only moves forward, and reports the share of its work actually done. Steps run in
 * parallel where they can (`run` returns at once; await it where its result is needed). A step
 * that is optional (a stale bake, a font server out of reach) is skipped when it fails; a required
 * one fails the load.
 */
import { StepError } from './failure';
import type { LoadingView } from './scene';

export interface StepDef {
  /** The line shown while it runs (Thai). */
  label: string;
  /** Its share of the bar, against the other steps' weights. */
  weight: number;
}

/** Reports a step's progress, 0–1. */
export type Report = (fraction: number) => void;

export class Preloader<K extends string> {
  private readonly done = new Map<K, number>();
  private readonly running: K[] = [];
  private readonly total: number;
  private shown = 0;
  /** Optional steps that failed and were skipped, with why. */
  readonly skipped: { step: K; error: string }[] = [];
  /** When each step started and ended (ms since the page started), for measuring. */
  readonly timings: Partial<Record<K, [number, number]>> = {};

  constructor(
    private readonly steps: Readonly<Record<K, StepDef>>,
    private view: LoadingView | null,
  ) {
    this.total = Object.values<StepDef>(steps).reduce((a, s) => a + s.weight, 0) || 1;
  }

  /** Shows progress on `view` from now on (a load started before its scene was shown). */
  attach(view: LoadingView): void {
    this.view = view;
    this.shown = 0;
    this.update();
  }

  /** Share of all the planned work done, 0–1. */
  get fraction(): number {
    let sum = 0;
    for (const [k, f] of this.done) sum += this.steps[k].weight * f;
    return sum / this.total;
  }

  /** Runs a required step: its failure fails the load, as a `StepError` naming the step. */
  async run<T>(step: K, work: (report: Report) => Promise<T> | T): Promise<T> {
    this.begin(step);
    try {
      return await work((f) => this.report(step, f));
    } catch (error) {
      throw error instanceof StepError ? error : new StepError(step, this.steps[step].label, error);
    } finally {
      this.end(step);
    }
  }

  /** Counts a step done whose work was just done in place (synchronous), timed from `since`. */
  mark(step: K, since = performance.now()): void {
    this.begin(step);
    this.timings[step] = [since, 0];
    this.end(step);
  }

  /** Runs a step that may fail without stopping the game: skipped (and noted) when it does. */
  async optional<T>(step: K, work: (report: Report) => Promise<T> | T): Promise<T | undefined> {
    this.begin(step);
    try {
      return await work((f) => this.report(step, f));
    } catch (error) {
      const why = error instanceof Error ? error.message : String(error);
      this.skipped.push({ step, error: why });
      console.warn(`Loading: skipped ${step} (${why})`);
      return undefined;
    } finally {
      this.end(step);
    }
  }

  private begin(step: K): void {
    this.timings[step] = [performance.now(), 0];
    this.running.push(step);
    this.done.set(step, 0);
    this.update();
  }

  private end(step: K): void {
    const t = this.timings[step];
    if (t) t[1] = performance.now();
    this.running.splice(this.running.indexOf(step), 1);
    this.done.set(step, 1);
    this.update();
  }

  private report(step: K, f: number): void {
    const was = this.done.get(step) ?? 0;
    this.done.set(step, Math.max(was, Math.min(1, f)));
    this.update();
  }

  private update(): void {
    if (!this.view) return;
    this.shown = Math.max(this.shown, this.fraction);
    this.view.progress(this.shown);
    // The latest step still under way names what is happening.
    const now = this.running.at(-1);
    if (now) this.view.step(this.steps[now].label);
  }
}

/**
 * Lets the browser paint (and the bar move) between chunks of work: the next animation frame, or
 * a short timeout when frames are not coming (a hidden tab), so a load never stalls in the background.
 */
export function nextFrame(): Promise<void> {
  return new Promise((done) => {
    let fired = false;
    const go = () => {
      if (fired) return;
      fired = true;
      done();
    };
    requestAnimationFrame(go);
    setTimeout(go, 50);
  });
}

/** Rejects if `p` has not settled within `ms` (a download hung, as opposed to failed). */
export function within<T>(p: Promise<T>, ms: number, what: string): Promise<T> {
  return new Promise<T>((ok, fail) => {
    const timer = setTimeout(() => fail(new Error(`${what}: no answer in ${ms / 1000}s`)), ms);
    p.then(
      (v) => {
        clearTimeout(timer);
        ok(v);
      },
      (e: unknown) => {
        clearTimeout(timer);
        fail(e instanceof Error ? e : new Error(String(e)));
      },
    );
  });
}
